<!--
  Internal only — not meant to be used directly outside this directory.
  Shared trigger/menu scaffolding for RfSelect (flat select/multi-select) and
  RfFilter (grouped filter): RfInputWrapper + RfButtonNew trigger + anchored RfMenu.
  Owns none of the selection state or item/group rendering — that's the caller's job via
  the default (trigger content) and #menu slots.
-->
<template>
  <RfInputWrapper
    ref="wrapperRef"
    v-bind="attrs"
    :title="title"
    :requiredTitle="requiredTitle"
    :labelFor="`rf-select-field-${uid}`"
    :variant="variant"
    :size="size"
    :autoWidth="autoWidth"
    :errorMessage="errorMessage"
    :hintMessage="hintMessage"
    :clearable="clearable"
    :hasValue="hasValue"
    wrapperClass="gap-2"
    @clear="emit('clear')"
  >
    <template v-if="slots.title" #title>
      <slot name="title" />
    </template>

    <!-- `#trigger` fully replaces the button below — for a caller that needs a real <input>
         as part of the trigger surface itself (can't validly nest one inside a <button>). It
         owns its own menu-toggle affordance; Shell just still renders/anchors the menu. -->
    <slot v-if="slots.trigger" name="trigger" />
    <RfButtonNew
      v-else
      :id="`rf-select-field-${uid}`"
      type="plain"
      size="x-small"
      :disabled="isTriggerDisabled"
      class="flex h-full min-h-9.5 w-full shrink-0 pr-2! pl-3! select-none *:h-full *:w-full *:items-center *:justify-between *:px-0!"
      :class="{ 'text-beige-30!': isTriggerDisabled }"
      aria-haspopup="listbox"
      :aria-expanded="!!menuEl?.opened"
      :aria-controls="menuEl?.opened ? menuEl?.menuId : undefined"
      :aria-invalid="!!errorMessage"
      :aria-describedby="wrapperRef?.messageId || undefined"
      @click="() => menuEl?.toggle?.()"
    >
      <slot />

      <RfProgressActivityIcon
        v-if="loading"
        class="fill-beige-30 my-auto h-6! w-6! shrink-0 animate-spin"
      />
      <RfChevronMiniClosedIcon
        v-else-if="!hideIcon"
        class="my-auto h-7! w-7! shrink-0 transition-transform duration-200"
        :class="{
          'rotate-180': menuEl?.opened,
          'fill-beige-10!': isTriggerDisabled,
          'fill-beige-50!': !isTriggerDisabled,
        }"
      />
    </RfButtonNew>

    <RfMenu
      ref="menuEl"
      :anchor="wrapperRef?.el"
      :preset="preset"
      :disabled="isTriggerDisabled"
      :sameWidth="sameWidth"
      :maxHeight="maxHeight ?? undefined"
      :placement="menuPlacement"
      role="listbox"
      @show="emit('show')"
      @hide="emit('hide')"
    >
      <template #menu>
        <slot name="menu" />
      </template>
    </RfMenu>
  </RfInputWrapper>
</template>

<script setup>
import { ref, useAttrs, useSlots, computed, getCurrentInstance } from "vue";
import RfMenu from "@/components/menus/RfMenu.vue";
import RfChevronMiniClosedIcon from "@/components/icons/RfChevronMiniClosedIcon.vue";
import RfProgressActivityIcon from "@/components/icons/RfProgressActivityIcon.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfInputWrapper from "@/components/inputs/RfInputWrapper.vue";

defineOptions({ inheritAttrs: false });
const { uid } = getCurrentInstance();
const slots = useSlots();
const attrs = useAttrs();
const emit = defineEmits(["show", "hide", "clear"]);

const props = defineProps({
  title: { type: String, default: "" },
  preset: { type: String, default: null },
  requiredTitle: { type: Boolean, default: false },
  variant: { type: String, default: "default" },
  size: { type: String, default: "base" },
  autoWidth: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
  hintMessage: { type: String, default: "" },
  clearable: { type: Boolean, default: false },
  hasValue: { type: Boolean, default: false },
  disabled: { type: [Boolean, String], default: false },
  loading: { type: Boolean, default: false },
  hideIcon: { type: Boolean, default: false },
  sameWidth: { type: Boolean, default: false },
  // floating-ui placement for the anchored menu; null = preset/global default
  menuPlacement: { type: String, default: null },
  maxHeight: { type: String, default: null },
});

const wrapperRef = ref();
const menuEl = ref();

const isTriggerDisabled = computed(() => props.disabled || props.loading);

defineExpose({
  opened: computed(() => menuEl.value?.opened),
  show: () => menuEl.value?.show?.(),
  hide: () => menuEl.value?.hide?.(),
  toggle: () => menuEl.value?.toggle?.(),
});
</script>
